import React, { useEffect, useState, useRef } from 'react'
import Layout from '../../Components/Layout'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaStar, FaMapMarkerAlt } from "react-icons/fa";
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import config from '../../config';
import TourListCard from '../../Components/Tour/ListCard';
import TourListCard2 from '../../Components/Tour/ListCard2';
import tourService from '../../services/tourService';
import { FaThLarge, FaList } from "react-icons/fa";

export default function TourList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    const [tourList, setTourList] = useState([]);
    const [prices, setPrices] = useState({
        min_price: 0,
        max_price: 5000
    });
    const [selectedDuration, setSelectedDuration] = useState(null);
    const [selectedTourType, setSelectedTourType] = useState([]);
    const [selectedDifficulty, setSelectedDifficulty] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [sortOption, setSortOption] = useState({type:'default', column:'default'});
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [pagination, setPagination] = useState({
        currentPage: 1,
        hasMore: false,
        totalPages: 0,
        total: 0
    });
    
    // Dynamic filter data from API
    const [tourTypes, setTourTypes] = useState([]);

    const durations = [
        { id: 1, name: 'Half Day', code: 'half' },
        { id: 2, name: 'Full Day', code: 'single' },
        { id: 3, name: 'Multi Day', code: 'multi' }
    ];

    const difficulties = [
        { id: 1, name: 'Easy', code: 'easy' },
        { id: 2, name: 'Moderate', code: 'moderate' },
        { id: 3, name: 'Challenging', code: 'challenging' }
    ];

    // API'den tour verilerini çek
    const fetchTours = async (page = 1, append = false) => {
        try {
            if (!append) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            setError(null);

            // URLSearchParams objesini oluştur ve page parametresini ekle
            const urlParams = new URLSearchParams(searchParams);
            urlParams.set('page', page);
            urlParams.set('perPage', 10);

            console.log('URL Params:', Object.fromEntries(urlParams));

            // Yeni searchToursFromUrlParams fonksiyonunu kullan
            const response = await tourService.searchToursFromUrlParams(urlParams);
            
            if (response && response.data) {
                if (append && page > 1) {
                    // Daha fazla yükle işlemi
                    setTourList(prevTours => [...prevTours, ...response.data]);
                } else {
                    // İlk yükleme veya yeni arama
                    setTourList(response.data);
                }

                // Pagination bilgilerini güncelle (API response'undan)
                setPagination({
                    currentPage: response.pagination?.currentPage || page,
                    hasMore: response.pagination?.hasMorePages || false,
                    totalPages: response.pagination?.totalPages || 0,
                    total: response.pagination?.total || response.data?.length || 0
                });
            } else {
                setTourList([]);
                setPagination({
                    currentPage: 1,
                    hasMore: false,
                    totalPages: 0,
                    total: 0
                });
            }
        } catch (err) {
            console.error('Error fetching tours:', err);
            setError(err.message);
            // Hata durumunda boş liste
            setTourList([]);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    // Tour types'ları API'den yükle
    useEffect(() => {
        const fetchTourTypes = async () => {
            try {
                const types = await tourService.getTourTypes();
                // API'den gelen format: [{id, name, slug}] - code olarak id kullanacağız
                const formattedTypes = types.map(type => ({
                    id: type.id,
                    name: type.name,
                    code: type.id // ID'yi code olarak kullanıyoruz çünkü API'ye id gönderiyoruz
                }));
                setTourTypes(formattedTypes);
            } catch (error) {
                console.error('Error fetching tour types:', error);
                // Hata durumunda fallback
                setTourTypes([]);
            }
        };

        fetchTourTypes();
    }, []);

    // URL parametrelerinden filter'ları initialize et
    useEffect(() => {
        // URL'den mevcut filter'ları oku
        const tourTypeIds = searchParams.get('tourTypeIds');
        const duration = searchParams.get('duration');
        const difficulty = searchParams.get('difficulty');
        
        if (tourTypeIds) {
            setSelectedTourType(tourTypeIds.split(','));
        }
        if (duration) {
            setSelectedDuration(duration);
        }
        if (difficulty) {
            setSelectedDifficulty(difficulty.split(','));
        }
    }, []);

    useEffect(() => {
        fetchTours();
    }, [searchParams]);

    const handleTourTypeChange = (code) => {
        setSelectedTourType(prev => {
            const newTypes = prev.includes(code) 
                ? prev.filter(item => item !== code)
                : [...prev, code];
            
            // URL parametrelerini güncelle (yeni format: tourTypeIds)
            const newParams = new URLSearchParams(searchParams);
            if (newTypes.length > 0) {
                newParams.set('tourTypeIds', newTypes.join(','));
            } else {
                newParams.delete('tourTypeIds');
            }
            setSearchParams(newParams);
            
            return newTypes;
        });
    };

    const handleDurationChange = (code) => {
        const newDuration = selectedDuration === code ? null : code;
        setSelectedDuration(newDuration);
        
        // URL parametrelerini güncelle
        const newParams = new URLSearchParams(searchParams);
        if (newDuration) {
            newParams.set('duration', newDuration);
        } else {
            newParams.delete('duration');
        }
        setSearchParams(newParams);
    };

    const handleDifficultyChange = (code) => {
        setSelectedDifficulty(prev => {
            const newDifficulties = prev.includes(code) 
                ? prev.filter(item => item !== code)
                : [...prev, code];
            
            // URL parametrelerini güncelle
            const newParams = new URLSearchParams(searchParams);
            if (newDifficulties.length > 0) {
                newParams.set('difficulty', newDifficulties.join(','));
            } else {
                newParams.delete('difficulty');
            }
            setSearchParams(newParams);
            
            return newDifficulties;
        });
    };

    const handleRangeChange = (values) => {
        setPrices({
            min_price: values[0],
            max_price: values[1]
        });
    };

    const handleResetFilters = () => {
        setSelectedDuration(null);
        setSelectedTourType([]);
        setSelectedDifficulty([]);
        setPrices({
            min_price: 0,
            max_price: 5000
        });

        // Yeni URL parametrelerini temizle
        const url = new URLSearchParams(searchParams);
        url.delete('tourTypeIds');
        url.delete('duration');
        url.delete('difficulty');
        url.delete('minPrice');
        url.delete('maxPrice');
        setSearchParams(url);
    };



    return (
        <Layout>
            <div className="breadcrumb-bar breadcrumb-bg-01 text-center">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">Tours</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center mb-0">
                                    <li className="breadcrumb-item"><a href="index.html"><i className="isax isax-home5"></i></a></li>
                                    <li className="breadcrumb-item">Tours</li>
                                    <li className="breadcrumb-item active" aria-current="page">Tour Lists</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container">
                    <div className="row d-flex flex-column-reverse flex-xl-row">
                        <div className="col-xl-3 col-lg-3 theiaStickySidebar">
                            <div className="card filter-sidebar mb-lg-0 mb-4">
                                <div className="card-body p-0">
                                    <form>
                                        <div className="accordion accordion-list">

                                            {/* Reset Filters Button */}
                                            <div className="p-3 border-bottom mt-8">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary w-100"
                                                    onClick={() => handleResetFilters()}
                                                >
                                                    <i className="isax isax-refresh me-2"></i>
                                                    Reset Filters
                                                </button>
                                            </div>

                                            {/* Price Range Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-price" aria-expanded="true"
                                                        aria-controls="accordion-price" role="button">
                                                        <i className="isax isax-coin text-primary me-2"></i>Price Range
                                                    </div>
                                                </div>
                                                <div id="accordion-price" className="accordion-collapse show collapse">
                                                    <div className="accordion-body d-flex flex-row">
                                                        <div style={{ paddingTop: "20px", width: "100%", margin: "0 auto" }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                                                <span style={{ fontWeight: 'bold', fontSize: '12px' }}>0 EUR</span>
                                                                <span style={{ fontWeight: 'bold', fontSize: '12px' }}>5000 EUR</span>
                                                            </div>
                                                            <RangeSlider
                                                                min={0}
                                                                max={5000}
                                                                value={[prices.min_price, prices.max_price]}
                                                                onInput={handleRangeChange}
                                                                thumbsDisabled={[false, false]}
                                                                rangeSlideDisabled={false}
                                                            />
                                                            <p style={{ textAlign: 'center' }} className='mt-4 mb-4'>
                                                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{prices.min_price} EUR</span> - 
                                                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}> {prices.max_price} EUR</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Duration Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-duration" aria-expanded="true"
                                                        aria-controls="accordion-duration" role="button">
                                                        <i className="isax isax-clock text-primary me-2"></i>Duration
                                                    </div>
                                                </div>
                                                <div id="accordion-duration" className="accordion-collapse show collapse">
                                                    <div className="accordion-body">
                                                        {durations.map((duration, index) => (
                                                            <div key={`duration-${index}`} className="form-check d-flex align-items-center mb-2 ps-0">
                                                                <input
                                                                    className="form-check-input ms-0 mt-0"
                                                                    name="duration"
                                                                    type="radio"
                                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                                    id={`duration-${index}`}
                                                                    value={duration.code}
                                                                    checked={selectedDuration === duration.code}
                                                                    onChange={() => handleDurationChange(duration.code)}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`duration-${index}`}>
                                                                    <span className="rating d-flex align-items-center">
                                                                        <span className="ms-2">{duration.name}</span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Tour Types Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-types" aria-expanded="true"
                                                        aria-controls="accordion-types" role="button">
                                                        <i className="isax isax-category text-primary me-2"></i>Tour Types
                                                    </div>
                                                </div>
                                                <div id="accordion-types" className="accordion-collapse show collapse">
                                                    <div className="accordion-body">
                                                        {tourTypes.map((type, index) => (
                                                            <div key={`type-${index}`} className="form-check d-flex align-items-center mb-2 ps-0">
                                                                <input
                                                                    className="form-check-input ms-0 mt-0"
                                                                    name="tourType"
                                                                    type="checkbox"
                                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                                    id={`type-${index}`}
                                                                    value={type.code}
                                                                    checked={selectedTourType.includes(type.code)}
                                                                    onChange={() => handleTourTypeChange(type.code)}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`type-${index}`}>
                                                                    <span className="rating d-flex align-items-center">
                                                                        <span className="ms-2">{type.name}</span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Difficulty Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-difficulty" aria-expanded="true"
                                                        aria-controls="accordion-difficulty" role="button">
                                                        <i className="isax isax-speedometer text-primary me-2"></i>Difficulty
                                                    </div>
                                                </div>
                                                <div id="accordion-difficulty" className="accordion-collapse show collapse">
                                                    <div className="accordion-body">
                                                        {difficulties.map((difficulty, index) => (
                                                            <div key={`difficulty-${index}`} className="form-check d-flex align-items-center mb-2 ps-0">
                                                                <input
                                                                    className="form-check-input ms-0 mt-0"
                                                                    name="difficulty"
                                                                    type="checkbox"
                                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                                    id={`difficulty-${index}`}
                                                                    value={difficulty.code}
                                                                    checked={selectedDifficulty.includes(difficulty.code)}
                                                                    onChange={() => handleDifficultyChange(difficulty.code)}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`difficulty-${index}`}>
                                                                    <span className="rating d-flex align-items-center">
                                                                        <span className="ms-2">{difficulty.name}</span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-9 col-lg-9">
                            <div className="d-flex align-items-center justify-content-between flex-wrap">
                                <h4 className="mb-3">Available Tours ({pagination.total || tourList.length})</h4>
                                <div className="d-flex align-items-center flex-wrap gap-3">
                                    {/* View Mode Toggle */}
                                    <div className="d-flex align-items-center border rounded mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                        <button
                                            className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-light'}`}
                                            onClick={() => setViewMode('grid')}
                                            style={{
                                                border: 'none',
                                                borderRadius: '4px 0 0 4px',
                                                padding: '8px 12px'
                                            }}
                                            title="Grid View"
                                        >
                                            <FaThLarge size={16} />
                                        </button>
                                        <button
                                            className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-light'}`}
                                            onClick={() => setViewMode('list')}
                                            style={{
                                                border: 'none',
                                                borderRadius: '0 4px 4px 0',
                                                padding: '8px 12px'
                                            }}
                                            title="List View"
                                        >
                                            <FaList size={16} />
                                        </button>
                                    </div>

                                    <div className="dropdown mb-3">
                                        <a href="javascript:void(0);" className="dropdown-toggle py-2" data-bs-toggle="dropdown" aria-expanded="false">
                                            <span className="fw-medium text-gray-9">Sort By : {sortOption.column === 'default' ? 'Recommended' : sortOption.column === 'price' ? 'Price' : sortOption.column === 'name' ? 'Name' : 'Rating'}</span>
                                        </a>
                                        <div className="dropdown-menu dropdown-sm">
                                            <h6 className="fw-medium fs-16 mb-3">Sort By</h6>
                                            <div className="form-check d-flex align-items-center ps-0 mb-2">
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    name="sort"
                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                    type="radio"
                                                    id="sort-default"
                                                    value="default"
                                                    checked={sortOption.type === 'default'}
                                                    onChange={() => setSortOption({type: 'default', column: 'default'})}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="sort-default">Recommended</label>
                                            </div>
                                            <div className="form-check d-flex align-items-center ps-0 mb-2">
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    name="sort"
                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                    type="radio"
                                                    id="sort-price-asc"
                                                    value="price-asc"
                                                    checked={sortOption.type === 'asc' && sortOption.column === 'price'}
                                                    onChange={() => setSortOption({type: 'asc', column: 'price'})}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="sort-price-asc">Price (Low to High)</label>
                                            </div>
                                            <div className="form-check d-flex align-items-center ps-0 mb-2">
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    name="sort"
                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                    type="radio"
                                                    id="sort-rating-desc"
                                                    value="rating-desc"
                                                    checked={sortOption.type === 'desc' && sortOption.column === 'rating'}
                                                    onChange={() => setSortOption({type: 'desc', column: 'rating'})}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="sort-rating-desc">Rating (High to Low)</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="tour-list">
                                <div className={`row ${viewMode === 'grid' ? 'y-gap-30' : ''} pt-30`}>
                                        {isLoading ? (
                                            <div className="col-12">
                                                <div className="d-flex flex-column align-items-center justify-content-center" style={{ marginBlock: '100px' }}>
                                                    <img src="/assets/gif/loading.gif" alt="Loading" style={{ width: "80px", height: "80px" }} />
                                                    <p className="mt-3 mb-0">Loading Tours...</p>
                                                </div>
                                            </div>
                                        ) : error ? (
                                            <div className="col-12">
                                                <div className="alert alert-danger" role="alert">
                                                    <h4 className="alert-heading">Error!</h4>
                                                    <p>{error}</p>
                                                    <hr />
                                                    <p className="mb-0">Please try again later or contact support if the problem persists.</p>
                                                </div>
                                            </div>
                                        ) : tourList && tourList.length > 0 ? (
                                            <>
                                                {tourList.map((tour, index) => (
                                                    viewMode === 'grid' ? (
                                                        <TourListCard 
                                                            key={tour.id}
                                                            tour={tour}
                                                        />
                                                    ) : (
                                                        <TourListCard2 
                                                            key={tour.id}
                                                            tour={tour}
                                                        />
                                                    )
                                                ))}
                                                
                                                {/* Load More Button */}
                                                {pagination.hasMore && (
                                                    <div className="col-12 text-center mt-4">
                                                        <button 
                                                            className="btn btn-primary"
                                                            onClick={() => fetchTours(pagination.currentPage + 1, true)}
                                                            disabled={isLoadingMore}
                                                        >
                                                            {isLoadingMore ? (
                                                                <div className="d-flex align-items-center justify-content-center">
                                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                        <span className="visually-hidden">Loading...</span>
                                                                    </div>
                                                                    Loading More Tours...
                                                                </div>
                                                            ) : 'Load More Tours'}
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="col-12">
                                                <div className="card mb-4">
                                                    <div className="card-body text-center py-5">
                                                        <div className="mb-4">
                                                            <i className="isax isax-search-normal" style={{ fontSize: "48px", color: "#6c757d" }}></i>
                                                        </div>
                                                        <h3 className="mb-3">No Tours Found</h3>
                                                        <p className="text-muted mb-4">
                                                            We couldn't find any tours matching your search criteria.
                                                        </p>
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => fetchTours()}
                                                        >
                                                            <i className="isax isax-refresh me-2"></i>
                                                            Try Again
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}
